import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyText from 'LuxKit/Typography/BodyText'
import { EmptyArray } from 'lib/array/arrayUtils'
import type { ReactNode } from 'react'
import { useContext } from 'react'
import { useIntl } from 'react-intl'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import FlashHotelReturnFlightsInclusion from 'components/OfferPage/FlashHotel/FlashHotelReturnFlightsInclusion'
import type { SvgIconProps } from 'LuxKit/SvgIcon'

interface Props {
  inclusions?: Array<App.OfferInclusion>
  maxHeight: number
  collapsedHeight?: number
  content?: string
  showMoreText?: string
  showLessText?: string
  heading?: string
  /** Rendered inside the expandable area after the inclusions list. */
  additionalContent?: ReactNode
  size?: 'small' | 'medium' | 'large'
  iconSize?: SvgIconProps['size']
}

function AccommodationOfferInclusionsExpandable({
  maxHeight,
  collapsedHeight,
  showLessText,
  showMoreText,
  inclusions = EmptyArray,
  content,
  heading,
  additionalContent,
  size = 'medium',
  iconSize,
}: Props) {
  const intl = useIntl()
  const { flights, rooms, charterBundleRoute } = useContext(OfferPageStateContext)
  const showReturnFlightsInclusion = flights && !charterBundleRoute

  const resolvedShowLessText = showLessText ?? intl.formatMessage({
    id: 'FNKxHD3fFrTa43MyF1At',
    defaultMessage: 'View less inclusions',
    description: 'Toggle text to collapse inclusions list on accommodation offer page',
  })
  const resolvedShowMoreText = showMoreText ?? intl.formatMessage({
    id: 'Z2TgaCuIlotsrKRmKce-',
    defaultMessage: 'View more inclusions',
    description: 'Toggle text to expand inclusions list on accommodation offer page',
  })

  return <VerticalSpacer gap={4}>
    {heading && inclusions.length > 0 && <BodyText variant="medium" weight="bold" colour="light-foreground-alt">{heading}</BodyText>}
    <LargeContentToggle
      maxHeight={maxHeight}
      collapsedHeight={collapsedHeight}
      showLessText={resolvedShowLessText}
      showMoreText={resolvedShowMoreText}
    >
      <VerticalSpacer gap={4}>
        {content && inclusions.length === 0 && <MarkdownRender fontSize={size} type="compact" content={content} />}
        {showReturnFlightsInclusion && <FlashHotelReturnFlightsInclusion rooms={rooms} size={size} />}
        {inclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} size={size} iconSize={iconSize} />)}
        {additionalContent}
      </VerticalSpacer>
    </LargeContentToggle>
  </VerticalSpacer>
}

export default AccommodationOfferInclusionsExpandable
